import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CHANGE_PASSWORD_MUTATION } from '../../apollo/queries';
import { useAuth } from '../../admin/AuthContext';
import '../css/ChangePasswordPage.css';

const ChangePasswordPageContent = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const { user, isAuthenticated } = useAuth();

    // GraphQL 비밀번호 변경 뮤테이션
    const [changePassword] = useMutation(CHANGE_PASSWORD_MUTATION, {
        onCompleted: (data) => {
            if (data.changePassword) {
                setIsLoading(false);
                setStep(3); // 완료 단계로 이동
            }
        },
        onError: (error) => {
            setIsLoading(false);
            console.error('비밀번호 변경 에러:', error);
            
            let errorMessage = '비밀번호 변경 중 오류가 발생했습니다.';
            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
                errorMessage = error.graphQLErrors[0].message;
            }
            setErrors({ general: errorMessage });
        }
    });

    // 로그인하지 않은 사용자는 홈으로 리다이렉트
    React.useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/');
        }
    }, [isAuthenticated, navigate]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // 에러 메시지 클리어
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.oldPassword.trim()) {
            newErrors.oldPassword = '현재 비밀번호를 입력해주세요.';
        }
        
        if (!formData.newPassword.trim()) {
            newErrors.newPassword = '새 비밀번호를 입력해주세요.';
        } else if (formData.newPassword.length < 6) {
            newErrors.newPassword = '비밀번호는 최소 6자 이상이어야 합니다.';
        }
        
        if (formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = '비밀번호가 일치하지 않습니다.';
        }
        
        if (formData.oldPassword === formData.newPassword) {
            newErrors.newPassword = '현재 비밀번호와 새 비밀번호가 같습니다.';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleConfirm = async () => {
        if (!validateForm()) {
            return;
        }

        setIsLoading(true);
        setErrors({});

        try {
            await changePassword({
                variables: {
                    oldPassword: formData.oldPassword,
                    newPassword: formData.newPassword
                }
            });
        } catch (error) {
            // 에러는 onError에서 처리됨
            console.error('비밀번호 변경 실행 에러:', error);
        }
    };

    const renderError = (fieldName) => {
        if (errors[fieldName]) {
            return <div className="cp-error-message" style={{ color: '#ff4444', fontSize: '14px', marginTop: '5px' }}>{errors[fieldName]}</div>;
        }
        return null;
    };

    const renderStepContent = () => {
        if (step === 1) {
            return (
                <div className="cp-step-container">
                    {errors.general && (
                        <div className="cp-error-banner" style={{ 
                            color: '#ff4444', 
                            backgroundColor: '#fff5f5',
                            border: '1px solid #ffdddd',
                            borderRadius: '4px',
                            padding: '10px',
                            marginBottom: '20px'
                        }}>
                            {errors.general}
                        </div>
                    )}
                    <div className="cp-form-grid">
                        <div className="cp-form-row">
                            <label>로그인 ID</label>
                            <div className="cp-form-field static">{user?.email || '로딩 중...'}</div>
                        </div>
                        <div className="cp-form-row">
                            <label htmlFor="oldPassword">현재 비밀번호</label>
                            <div className="cp-form-field">
                                <input 
                                    type="password" 
                                    id="oldPassword" 
                                    name="oldPassword"
                                    value={formData.oldPassword}
                                    onChange={handleInputChange}
                                    placeholder="현재 비밀번호를 입력해 주세요."
                                    disabled={isLoading}
                                />
                                {renderError('oldPassword')}
                            </div>
                        </div>
                        <div className="cp-form-row">
                            <label htmlFor="newPassword">새로운 비밀번호</label>
                            <div className="cp-form-field">
                                <input 
                                    type="password" 
                                    id="newPassword" 
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleInputChange}
                                    placeholder="새 비밀번호를 입력해 주세요."
                                    disabled={isLoading}
                                />
                                {renderError('newPassword')}
                            </div>
                        </div>
                        <div className="cp-form-row">
                            <label htmlFor="confirmPassword">비밀번호 재입력</label>
                            <div className="cp-form-field">
                                <input 
                                    type="password" 
                                    id="confirmPassword" 
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleInputChange}
                                    placeholder="확인을 위해 다시 입력해 주세요."
                                    disabled={isLoading}
                                />
                                {renderError('confirmPassword')}
                            </div>
                        </div>
                    </div>
                    <div className="cp-notes">
                        <ul>
                            <li>최소 6자 이상 입력해 주세요.</li>
                            <li>영문과 숫자를 모두 포함하는 것을 권장합니다.</li>
                            <li>현재 비밀번호와 다른 비밀번호를 입력해 주세요.</li>
                        </ul>
                    </div>
                    <div className="cp-actions">
                        <button 
                            className="cp-confirm-btn-large" 
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {isLoading ? '변경 중...' : '입력 완료'}
                        </button>
                    </div>
                </div>
            );
        } else if (step === 2) {
            return (
                <div className="cp-step-container">
                    <div className="cp-summary-grid">
                        <div className="cp-summary-row">
                            <span className="label">로그인 ID</span>
                            <span>{user?.email || '로딩 중...'}</span>
                        </div>
                        <div className="cp-summary-row">
                            <span className="label">새로운 비밀번호</span>
                            <span>{"*".repeat(formData.newPassword.length)}</span>
                        </div>
                    </div>
                    <div className="cp-actions space-between">
                        <button className="cp-back-btn" onClick={() => setStep(1)} disabled={isLoading}>돌아가기</button>
                        <button className="cp-confirm-btn" onClick={handleConfirm} disabled={isLoading}>
                            {isLoading ? '변경 중...' : '확인'}
                        </button>
                    </div>
                </div>
            );
        } else if (step === 3) {
            return (
                <div className="cp-step-container centered-content">
                    <h2 className="cp-final-title">비밀번호 변경이 완료되었습니다.</h2>
                    <div className="cp-actions">
                        <button className="cp-confirm-btn-large" onClick={() => navigate('/my-page')}>완료</button>
                    </div>
                </div>
            );
        }
    };

    return (
        <div className="change-password-page">
            <div className="cp-breadcrumb">
                <span>홈</span> &gt; <span>내 정보</span> &gt; <span className="current">비밀번호 변경</span>
            </div>
            <h1 className="cp-title">비밀번호 변경</h1>
            {renderStepContent()}
        </div>
    );
};

const ChangePasswordPage = () => {
    return (
        <ChangePasswordPageContent />
    );
};

export default ChangePasswordPage; 